import Cookies from "js-cookie";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiFactory from "../api";
import { registerToken } from "../api/fcm";
import {
  chatSocket,
  closeAuthSocket,
  closeChatSocket,
  initAuthSocket,
  initChatSocket,
} from "../api/webSocket";
import { useInfoUser } from "../store/UserStore";
import PeerConnection from "../api/communication/PeerConnection";

const CallingContext = createContext(null);

export const useCallingContext = () => {
  return useContext(CallingContext);
};

export const CallingProvider = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateUser, updateLanguageMap, updateStickerKeywords } = useInfoUser();

  const [callFrom, setCallFrom] = useState("");
  const [friend, setFriend] = useState({
    video: true,
    audio: true,
  });
  const [me, setMe] = useState({
    video: true,
    audio: true,
  });
  const [callingId, setCallingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [localSrc, setLocalSrc] = useState(null);
  const [peerSrc, setPeerSrc] = useState(null);
  const [isCalling, setIsCalling] = useState(false);
  const [isEndCalled, setIsEndCalled] = useState(false);
  const [enableVideo, setEnableVideo] = useState(true);
  const [enableAudio, setEnableAudio] = useState(true);
  const [isHost, setIsHost] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [callingStatus, setCallingStatus] = useState();

  const callWindowRef = useRef(null);

  const pcRef = useRef({});

  const startCall = async (isCaller, friendId, me, callingId, mode) => {
    const friendResult = await fetchFriend(friendId, mode);

    const peer = new PeerConnection(
      friendResult,
      me,
      callingId,
      chatSocket,
      setCallingStatus
    );

    peer.subscribe(({ event, stream }) => {
      if (event === "localStream") {
        setLocalSrc(stream);
      }

      if (event === "peerStream") {
        if (!stream) return;

        setPeerSrc(stream);
      }
    });

    pcRef.current = peer;

    await peer.start(isCaller);

    setIsHost(isCaller);
  };

  const fetchFriend = async (friendId, mode) => {
    setIsLoading(true);
    const result = await apiFactory.userApi.getFriend(friendId);
    setFriend({
      ...result?.data,
      audio: true,
      video: mode === "video",
    });
    setIsLoading(false);

    return {
      ...result?.data,
      audio: true,
      video: mode === "video",
    };
  };

  const endCall = (isTerminator) => {
    if (callWindowRef.current && !callWindowRef.current.closed) {
      callWindowRef.current.close();
      callWindowRef.current = null; // optional: clean up the ref
    }

    if (isTerminator) pcRef.current.stop(isTerminator);

    window.close();
  };

  const toggleVideo = (enabled) => {
    setMe({
      ...me,
      video: enabled,
    });

    chatSocket.send(
      JSON.stringify({
        event: "setting",
        to: friend,
        from: {
          ...me,
          video: enabled,
        },
        callingId: callingId,
      })
    );
    pcRef?.current?.toggleVideo(enabled);
  };

  const toggleAudio = (enabled) => {
    setMe({
      ...me,
      audio: enabled,
    });

    chatSocket.send(
      JSON.stringify({
        event: "setting",
        to: friend,
        from: {
          ...me,
          audio: enabled,
        },
        callingId: callingId,
      })
    );
    pcRef?.current?.toggleAudio(enabled);
  };

  const getMe = async () => {
    if (user) return;

    const me = await apiFactory.userApi.getMe();
    if (me?.status === 200) {
      updateUser(me?.data?.user);
      updateLanguageMap(me?.data?.languageMap);
      updateStickerKeywords(me?.data?.stickerKeywords);
      await registerToken(me?.data?.user?.userId);
      return me?.data?.user;
    } else {
      toast.error(me?.message);
      updateUser(null);
      updateLanguageMap(null);
      Cookies.remove("access_token");
      navigate("/login");
    }
  };

  const processWebSocket = () => {
    chatSocket.onmessage = async (event) => {
      if (typeof event.data === "string") {
        if (event.data === "PING") {
          return;
        }
      }

      const data = JSON.parse(event?.data);

      switch (data?.event) {
        case "session-uuid":
          sessionStorage.setItem("sessionUuid", data?.sessionUuid);
          break;
        case "candidate":
          if (!data?.candidate) return;

          setFriend((prev) => {
            return {
              ...prev,
              video: data?.from?.video,
              audio: data?.from?.audio,
            };
          });
          await pcRef?.current?.addIceCandidate(data?.candidate);
          break;
        case "offer":
          await pcRef?.current?.setRemoteDescription(
            new RTCSessionDescription(data?.sdp)
          );
          await pcRef?.current?.createAnswer();
          break;
        case "answer":
          await pcRef?.current?.setRemoteDescription(
            new RTCSessionDescription(data?.sdp)
          );
          break;
        case "setting":
          setFriend((prev) => {
            return {
              ...prev,
              video: data?.from?.video,
              audio: data?.from?.audio,
            };
          });
          break;
        case "end":
        case "busy":
        case "reject":
          endCall(false);
          break;
        case "closed":
          setIsClosed(true);
          break;
        default:
          break;
      }
    };

    window.onbeforeunload = function () {
      closeChatSocket();
      closeAuthSocket();
    };
  };

  const process = async () => {
    const meResult = await getMe();
    initChatSocket();
    initAuthSocket();

    const callerId = searchParams?.get("caller_id");
    const calleeId = searchParams?.get("callee_id");
    const calling_id = searchParams?.get("calling_id");
    const mode = searchParams?.get("mode");

    setMe({
      ...meResult,
      video: mode === "video",
      audio: true,
    });
    setCallingId(searchParams?.get("calling_id"));

    if (calleeId) {
      await startCall(
        true,
        calleeId,
        {
          ...meResult,
          video: mode === "video",
          audio: true,
        },
        calling_id,
        mode
      );
    } else {
      await startCall(
        false,
        callerId,
        {
          ...meResult,
          video: mode === "video",
          audio: true,
        },
        calling_id,
        mode
      );
    }

    if (mode === "audio") {
      setEnableVideo(false);
    }

    setIsCalling(true);
  };

  useEffect(() => {
    process();
  }, []);

  useEffect(() => {
    if (user && chatSocket) {
      processWebSocket();
    }
  }, [user, chatSocket, isCalling]);

  const values = useMemo(
    () => ({
      callFrom,
      setCallFrom,
      friend,
      setFriend,
      isLoading,
      setIsLoading,
      localSrc,
      setLocalSrc,
      peerSrc,
      setPeerSrc,
      isCalling,
      setIsCalling,
      isEndCalled,
      setIsEndCalled,
      endCall,
      pcRef,
      callWindowRef,
      me,
      toggleVideo,
      toggleAudio,
      enableVideo,
      enableAudio,
      isHost,
      isClosed,
      callingStatus,
    }),
    [
      callFrom,
      friend,
      isLoading,
      localSrc,
      peerSrc,
      isCalling,
      isEndCalled,
      me,
      enableVideo,
      enableAudio,
      isHost,
      isClosed,
      callingStatus,
    ]
  );
  return (
    <CallingContext.Provider value={values}>{children}</CallingContext.Provider>
  );
};
